import { useState } from 'react';
import { Phone, Mail, Globe, FileText, AlertTriangle } from 'lucide-react';
import { useI18n } from '../lib/i18n';

/**
 * CON QUIÉN SE CONFIRMÓ LA FECHA DEL CHEQUE — el diálogo que faltaba.
 *
 * `claim_check_dates.sent_on` arranca y detiene el reloj de payments y era el campo más blando
 * que alimenta un bono: en 1035 de 1390 filas no decía quién lo escribió. Desde la mig 0376 la
 * base exige autor, adjuster y vía, así que el botón de "cheque enviado" ya no puede ser un
 * click a secas: tiene que preguntar.
 *
 * Se usa desde los TRES lugares que registran la fecha (tarjeta del claim, cola de payments y
 * panel de pagos), para que la regla se pida igual en todos y no haya un atajo por donde entre
 * una fila sin confirmar.
 *
 * La nota solo se vuelve obligatoria en el caso que el backend rechaza: teléfono sin llamada
 * ligada. Pedirla siempre molesta y termina llenándose de "ok".
 */

export type CheckSource = 'phone' | 'email' | 'portal' | 'carrier_letter';
export type CheckConfirm = { adjusterName: string; source: CheckSource; notes: string | null; reason: string | null };

const VIAS: Array<{ v: CheckSource; icon: React.ReactNode }> = [
  { v: 'phone', icon: <Phone size={13} /> },
  { v: 'email', icon: <Mail size={13} /> },
  { v: 'portal', icon: <Globe size={13} /> },
  { v: 'carrier_letter', icon: <FileText size={13} /> },
];

export function CheckConfirmDialog({ onConfirm, onCancel, needsReason }: {
  onConfirm: (c: CheckConfirm) => void;
  onCancel: () => void;
  /** true cuando ya se editó antes: el backend va a exigir la razón. */
  needsReason?: boolean;
}) {
  const { t } = useI18n();
  const [adjusterName, setAdjuster] = useState('');
  const [source, setSource] = useState<CheckSource>('phone');
  const [notes, setNotes] = useState('');
  const [reason, setReason] = useState('');

  // Mismas reglas que el backend, para que el error se vea antes de enviar y no como un 400.
  const faltaNota = source === 'phone' && !notes.trim();
  const listo = adjusterName.trim().length > 0 && !faltaNota && (!needsReason || reason.trim().length > 0);

  return (
    <div style={{ border: '1px solid var(--line)', borderRadius: 10, padding: 12, background: 'var(--bg)', display: 'grid', gap: 8, marginTop: 8 }}>
      <div style={{ fontSize: 'var(--fs-2xs)', fontWeight: 'var(--fw-bold)' as React.CSSProperties['fontWeight'] }}>{t('ckc.title')}</div>
      <label style={{ fontSize: 'var(--fs-2xs)' }}>{t('ckc.adjuster')} <span style={{ color: 'var(--red)' }}>*</span>
        <input autoFocus value={adjusterName} onChange={(e) => setAdjuster(e.target.value)} placeholder={t('ckc.adjusterPh')} style={{ width: '100%' }} />
      </label>
      <div>
        <div style={{ fontSize: 'var(--fs-2xs)', marginBottom: 4 }}>{t('ckc.source')} <span style={{ color: 'var(--red)' }}>*</span></div>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {VIAS.map(({ v, icon }) => (
            <button key={v} type="button" onClick={() => setSource(v)}
              className={source === v ? 'btn sm' : 'btn ghost sm'} style={{ fontSize: 'var(--fs-2xs)' }}>
              {icon} {t(`ckc.source.${v}`)}
            </button>
          ))}
        </div>
      </div>
      {source === 'phone' && (
        <label style={{ fontSize: 'var(--fs-2xs)' }}>{t('ckc.notes')} <span style={{ color: 'var(--red)' }}>*</span>
          <input value={notes} onChange={(e) => setNotes(e.target.value)} placeholder={t('ckc.notesPh')} style={{ width: '100%' }} />
        </label>
      )}
      {needsReason && (
        <label style={{ fontSize: 'var(--fs-2xs)' }}>{t('ckc.reason')} <span style={{ color: 'var(--red)' }}>*</span>
          <input value={reason} onChange={(e) => setReason(e.target.value)} placeholder={t('ckc.reasonPh')} style={{ width: '100%' }} />
        </label>
      )}
      {faltaNota && <div className="muted" style={{ fontSize: 'var(--fs-2xs)' }}><AlertTriangle size={11} /> {t('ckc.phoneHint')}</div>}
      <div style={{ display: 'flex', gap: 8 }}>
        <button className="btn sm" disabled={!listo}
          onClick={() => onConfirm({ adjusterName: adjusterName.trim(), source, notes: notes.trim() || null, reason: reason.trim() || null })}>
          {t('ckc.save')}
        </button>
        <button className="btn ghost sm" onClick={onCancel}>{t('ckc.cancel')}</button>
      </div>
    </div>
  );
}
